import { otpChallengeFrom, otpChannel, requestWithOtpSecurity, solveLoginSecurityCheck, type OtpChallenge } from "@templeos/shared/otp-browser";
import { useOtpFlow } from "@templeos/shared/otp-react";
import { formatINR } from "@templeos/shared";
import { Alert, Button, Card, Checkbox, MoneyField, TextField } from "@templeos/ui";
import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { api, ApiError, errorText, NetworkError } from "../lib/api.js";
import { passwordErrors } from "../lib/rules.js";
import { COUNTER_KEY, engine, local, useSession } from "../session.js";

function Centered({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children: ReactNode }) {
  const { profile } = useSession();
  return (
    <div className="pos-center">
      <Card>
        <div className="tos-stack">
          <div>
            <div className="tos-muted" style={{ fontWeight: 700 }}>{profile?.name ?? "TempleOS"} · Counter POS</div>
            <h1>{title}</h1>
            {subtitle && <div className="tos-muted">{subtitle}</div>}
          </div>
          {children}
        </div>
      </Card>
    </div>
  );
}

/** Sign in at a counter (the counter code ties the session, shift and receipts to that window). */
export function Login() {
  const s = useSession();
  const [f, setF] = useState({ username: "", password: "", counterCode: local.get(COUNTER_KEY) ?? "", totp: "" });
  const [takeover, setTakeover] = useState(false);
  const [needTakeover, setNeedTakeover] = useState(false);
  const [needTotp, setNeedTotp] = useState(false);
  const [security, setSecurity] = useState<{ nonce: string; bits: number } | null>(null);
  const [solved, setSolved] = useState<{ nonce: string; counter: string } | null>(null);
  const [progress, setProgress] = useState(0);
  const requireSecurity = (error: ApiError) => {
    if (error.code !== "CHALLENGE_REQUIRED") return;
    setSecurity(error.details as { nonce: string; bits: number }); setSolved(null); setProgress(0);
  };
  const [errors, setErrors] = useState<Record<string, string>>({});
  const otp = useOtpFlow({
    fallback: async (current) => api.post<OtpChallenge>("/api/auth/otp/fallback", { ...f, takeover, challenge: solved ?? undefined, challengeId: current.challengeId }, 45_000)
      .catch((error) => { if (error instanceof ApiError) requireSecurity(error); throw error; }),
    errorMessage: (error) => error instanceof NetworkError ? "The temple server cannot be reached. Signing in needs the network." : errorText(error),
  });
  const update = (value: Partial<typeof f>) => { otp.clear(); setF({ ...f, ...value }); };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const fe: Record<string, string> = {};
    if (!f.username.trim()) fe.username = "Enter your username";
    if (!f.password) fe.password = "Enter your password";
    if (!/^[A-Za-z0-9-]{2,12}$/.test(f.counterCode.trim())) fe.counterCode = "Enter the counter code shown on the counter, e.g. C1 or G1";
    setErrors(fe);
    if (Object.keys(fe).length) return;
    void otp.perform(async (signal) => {
      try {
        await requestWithOtpSecurity((recaptchaToken) => s.login({ ...f, takeover, recaptchaToken, challenge: solved ?? undefined,
          otp: otp.challenge ? { challengeId: otp.challenge.challengeId, code: otp.code } : undefined }), signal);
        return null;
      } catch (error) {
        if (error instanceof ApiError) {
          requireSecurity(error);
          const challenge = error.code === "OTP_REQUIRED" ? otpChallengeFrom(error.details) : null;
          if (challenge) return challenge;
          if (error.code === "SESSION_ACTIVE") setNeedTakeover(true);
          if (error.code === "TOTP_REQUIRED" || error.code === "TOTP_INVALID") setNeedTotp(true);
          setErrors(error.fieldErrors());
        }
        throw error;
      }
    }, (challenge) => { if (challenge) otp.accept(challenge); else otp.clear(); });
  };
  const completeSecurity = () => {
    if (!security) return;
    setProgress(0.01);
    void otp.perform((signal) => solveLoginSecurityCheck(security.nonce, security.bits, setProgress, signal), (counter) => {
      setSolved({ nonce: security.nonce, counter }); setProgress(1);
    });
  };
  return (
    <Centered title="Counter sign-in" subtitle="Use your own login. Every bill is recorded against you and this counter.">
      <form className="tos-stack" onSubmit={submit} noValidate>
        <TextField label="Username" required autoFocus autoComplete="username" value={f.username} disabled={otp.busy} error={errors.username} onChange={(event) => update({ username: event.target.value })} />
        <TextField label="Password" required type="password" autoComplete="current-password" value={f.password} disabled={otp.busy} error={errors.password} onChange={(event) => update({ password: event.target.value })} />
        <TextField label="Counter code" required value={f.counterCode} disabled={otp.busy} error={errors.counterCode} maxLength={12} placeholder="C1" hint="Printed on the counter window (C1 to C6, gates G1 and G2)"
          onChange={(event) => update({ counterCode: event.target.value.toUpperCase() })} />
        {needTotp && <TextField label="Authenticator code" required inputMode="numeric" maxLength={6} disabled={otp.busy} value={f.totp} onChange={(event) => setF({ ...f, totp: event.target.value })} />}
        {needTakeover && <Checkbox label="End my session on the other device and continue here" checked={takeover} onChange={setTakeover} />}
        {otp.challenge && <>
          <Alert tone="info">Enter the code sent to {otp.challenge.phoneMasked ?? "your registered mobile"}. {otpChannel(otp.challenge) === "whatsapp" ? "Check WhatsApp." : "Check your SMS messages."} Use the newest code after delivery switches.
            {otp.challenge.devCode && <> Demo code: <strong>{otp.challenge.devCode}</strong>.</>}
          </Alert>
          <TextField label="Mobile verification code" required inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={otp.code} disabled={otp.busy}
            onChange={(event) => otp.setCode(event.target.value.replace(/\D/g, ""))} />
          {typeof otp.challenge.fallbackAfterSeconds === "number" && <Button disabled={otp.busy} onClick={() => void otp.fallback()}>Code delayed? Try another delivery method</Button>}
          <Button disabled={otp.busy} onClick={otp.clear}>Restart mobile verification</Button>
        </>}
        {security && <div className="tos-stack" role="group" aria-label="Security check">
          <p role="status">{solved ? "Security check complete." : otp.busy && progress > 0 ? "Completing the security check…" : "Complete the security check before signing in."}</p>
          {!solved && <Button disabled={otp.busy} onClick={completeSecurity}>Complete security check</Button>}
        </div>}
        {otp.error && <Alert tone="danger">{otp.error}</Alert>}
        <Button type="submit" variant="primary" size="lg" block loading={otp.busy} disabled={Boolean(security) && !solved}>Sign in</Button>
        <div className="tos-row tos-muted" style={{ justifyContent: "space-between" }}>
          <Link to="/board">Darshan availability board</Link><Link to="/customer-display">Customer display</Link>
        </div>
      </form>
    </Centered>
  );
}

/** Password expired or reset by the admin: change it before billing (SOW Module 72 policy). */
export function ChangePassword() {
  const s = useSession();
  const [f, setF] = useState({ current: "", next: "", again: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const fe = passwordErrors(f, s.me?.username ?? "");
    setErrors(fe);
    if (Object.keys(fe).length) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/auth/change-password", { currentPassword: f.current, newPassword: f.next });
      await s.refreshAfterPasswordChange(f.next);
    } catch (err) {
      const server = err instanceof ApiError ? err.fieldErrors() : {};
      // The server names the fields currentPassword and password (or newPassword).
      const mapped = { current: server.currentPassword, next: server.password ?? server.newPassword };
      if (mapped.current || mapped.next) setErrors(Object.fromEntries(Object.entries(mapped).filter(([, v]) => v)) as Record<string, string>);
      else setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Centered title="Choose a new password" subtitle="Your password has expired or was reset. At least 10 characters with letters and numbers.">
      <form className="tos-stack" onSubmit={submit} noValidate>
        <TextField label="Current password" required type="password" autoComplete="current-password" value={f.current} error={errors.current} onChange={(e) => setF({ ...f, current: e.target.value })} />
        <TextField label="New password" required type="password" autoComplete="new-password" value={f.next} error={errors.next} onChange={(e) => setF({ ...f, next: e.target.value })} />
        <TextField label="New password again" required type="password" autoComplete="new-password" value={f.again} error={errors.again} onChange={(e) => setF({ ...f, again: e.target.value })} />
        {error && <Alert tone="danger">{error}</Alert>}
        <Button type="submit" variant="primary" size="lg" block loading={busy}>Change password</Button>
        <Button variant="ghost" onClick={() => void s.logout()}>Sign out</Button>
      </form>
    </Centered>
  );
}

/** Open a cashier shift with the float already in the drawer. */
export function OpenShift() {
  const s = useSession();
  const [float, setFloat] = useState<number | null>(null);
  const [floatError, setFloatError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (float === null) return setFloatError("Enter the opening cash in the drawer (0 if empty).");
    setFloatError(null);
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/core/shifts/open", { openingFloat: float });
      await s.refreshShift();
    } catch (err) {
      const field = err instanceof ApiError ? err.fieldErrors().openingFloat : undefined;
      if (field) setFloatError(field);
      else setError(err instanceof NetworkError ? "Opening a shift needs the server. Check the network." : errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Centered title="Open your shift" subtitle={`${s.me?.fullName ?? ""} at ${s.me?.counter ? `${s.me.counter.code} · ${s.me.counter.name}` : "this counter"}`}>
      <form className="tos-stack" onSubmit={submit} noValidate>
        <MoneyField label="Opening cash in drawer (₹)" required autoFocus value={float} error={floatError} onChange={(v) => { setFloat(v); setFloatError(null); }}
          hint="Count the float handed over by the supervisor." />
        {error && <Alert tone="danger">{error}</Alert>}
        <Button type="submit" variant="primary" size="lg" block loading={busy}>Open shift{float ? ` with ${formatINR(float)}` : ""}</Button>
        <Button variant="ghost" onClick={() => void s.logout()}>Sign out</Button>
      </form>
    </Centered>
  );
}

/** The cashier's open shift belongs to another counter: bills would be booked there, so billing is blocked. */
export function WrongCounter() {
  const s = useSession();
  const sh = s.shift?.shift;
  return (
    <Centered title="Shift open at another counter">
      <Alert tone="warning">Your open shift is at {sh?.counter_code} ({sh?.counter_name}). Close it there, or sign in at {sh?.counter_code}.</Alert>
      <Button variant="primary" onClick={() => void s.logout()}>Sign out</Button>
    </Centered>
  );
}

/** Auto-lock screen: re-enter the password to continue (works offline too). */
export function LockScreen() {
  const s = useSession();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmSwitch, setConfirmSwitch] = useState(false); // shown in the lock screen itself: a dialog would sit behind it
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!password) return setError("Enter your password.");
    setBusy(true);
    setError(null);
    try {
      if (!(await s.unlock(password))) setError("Incorrect password.");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setPassword("");
      setBusy(false);
    }
  };
  const switchUser = () => {
    if (engine.state.pending && !confirmSwitch) setConfirmSwitch(true);
    else void s.logout();
  };
  return (
    <div className="pos-lock" role="dialog" aria-modal="true" aria-label="Screen locked">
      <Centered title="Screen locked" subtitle={`${s.me?.fullName ?? ""} · locked after 5 minutes without activity`}>
        <form className="tos-stack" onSubmit={submit} noValidate>
          <TextField label="Password" required type="password" autoFocus autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          {error && <Alert tone="danger">{error}</Alert>}
          <Button type="submit" variant="primary" size="lg" block loading={busy}>Unlock</Button>
          {confirmSwitch && (
            <Alert tone="warning">
              {engine.state.pending} bill(s) by {s.me?.fullName} are still on this terminal only. They upload when that cashier signs in again.
            </Alert>
          )}
          <Button variant={confirmSwitch ? "danger" : "ghost"} onClick={switchUser}>{confirmSwitch ? "Sign out anyway" : "Switch cashier"}</Button>
          {confirmSwitch && <Button variant="ghost" onClick={() => setConfirmSwitch(false)}>Keep this cashier</Button>}
        </form>
      </Centered>
    </div>
  );
}
